import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { ClockCountdownIcon } from "@phosphor-icons/react";
import { fetchScoreHistory } from "../lib/api";
import { scoreColor } from "../lib/format";

const fmtT = (ts) => {
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export const ScoreHistory = ({ mint }) => {
  const { data } = useQuery({
    queryKey: ["scoreHistory", mint],
    queryFn: () => fetchScoreHistory(mint),
    refetchInterval: 120000,
    staleTime: 60000,
  });

  const points = data?.points || [];
  const move = data?.move;

  return (
    <div className="border border-white/10 bg-card p-5" data-testid="score-history">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading font-bold text-lg flex items-center gap-2">
          <ClockCountdownIcon size={18} className="text-neon" /> Score History
        </h2>
        {move && (
          <span data-testid="score-move" className="font-mono text-[11px] px-2 py-0.5 border"
            style={{ color: move.delta >= 0 ? "#00E599" : "#FF3366", borderColor: (move.delta >= 0 ? "#00E599" : "#FF3366") + "40" }}>
            {move.from} → {move.to} ({move.delta >= 0 ? "+" : ""}{move.delta}) · {move.spanHours}h
          </span>
        )}
      </div>

      {points.length < 2 ? (
        <p className="text-[12px] text-muted-foreground leading-relaxed mt-2" data-testid="score-history-empty">
          Building this token's score timeline. We snapshot the Degen Score every 15 minutes — check back soon to see how it moves over time.
        </p>
      ) : (
        <>
          <div className="mt-3">
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={points.map((p) => ({ t: fmtT(p.ts), score: p.score }))} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="t" stroke="#666" fontSize={10} tickLine={false} axisLine={false} reversed />
                <YAxis stroke="#666" fontSize={10} tickLine={false} axisLine={false} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
                <Tooltip
                  contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}
                  labelStyle={{ color: "#888" }}
                  formatter={(v) => [v, "Score"]}
                />
                <Line type="monotone" dataKey="score" stroke="#00E599" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-muted-foreground/70 mt-2 font-mono">
            {points.length} snapshots · newest on the left · a rising score means signals are strengthening
          </p>
        </>
      )}
    </div>
  );
};
